import { useState } from "react";
import { CSSObject } from "@emotion/react";
import { ChoiceButton } from "swing-components";
import { translate } from "utils/string-table";

import {
  ModalSubFeedbackBody,
  ModalSubFeedbackFooter,
  ModalSubFeedbackHeader,
  NegativeData,
  NegativeStep,
} from "~components/ModalSubFeedback";

/********* Styles ********/
const styles: Record<string, CSSObject> = {
  label: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "normal",
  },
  subLabel: {
    fontSize: 12,
    lineHeight: "normal",
    // Override the <p/> margin set by
    // ion-modal.modal-sub-feedback
    marginBottom: "16px !important",
  },
  choiceButtonsContainer: {
    display: "flex",
    gap: 16,
  },
};

/********* Types ********/
type ModalSubFeedbackContentNegativeBlockSubProps = {
  subName?: string;
  schoolName: string;
  negativeData: NegativeData;
  onDismiss: () => void;
  goBackToRatingStep: () => void;
  setNegativeData: (data: NegativeData) => void;
  setNegativeStep: (step: NegativeStep) => void;
};

/********* Component ********/
export function ModalSubFeedbackContentNegativeBlockSub(
  props: ModalSubFeedbackContentNegativeBlockSubProps,
) {
  const {
    goBackToRatingStep,
    negativeData,
    onDismiss,
    setNegativeData,
    setNegativeStep,
    subName,
    schoolName,
  } = props;

  /********* Hooks ********/
  const [_isBlocked, _setIsBlocked] = useState<boolean | undefined>(negativeData.isBlocked);

  /********* Handlers ********/
  function handleOnNext() {
    const updatedData = {
      ...negativeData,
      isBlocked: _isBlocked,
    };

    setNegativeData(updatedData);
    setNegativeStep("reasons");
  }

  /********* Render ********/
  return (
    <>
      <ModalSubFeedbackHeader title={translate("GIVE_FEEDBACK_LABEL")} onDismiss={onDismiss} />
      <ModalSubFeedbackBody>
        <p css={styles.label}>{translate("MSF_NEGATIVE_BLOCK_LABEL")}</p>
        <p css={styles.subLabel}>
          {translate("MSF_NEGATIVE_BLOCK_SUB_LABEL", { subName, schoolName })}
        </p>
        <div css={styles.choiceButtonsContainer}>
          <ChoiceButton
            icon={{ name: "Check Circle", color: "green500" }}
            label={translate("MSF_NEGATIVE_BLOCK_FIRST_CHOICE")}
            value="false"
            isSelected={_isBlocked === false}
            onChange={(isSubBlocked: string) => _setIsBlocked(JSON.parse(isSubBlocked))}
            activeBgColor="green200"
          />
          <ChoiceButton
            icon={{ name: "Person Off", color: "red500" }}
            label={translate("MSF_NEGATIVE_BLOCK_SECOND_CHOICE")}
            value="true"
            isSelected={_isBlocked === true}
            onChange={(isSubBlocked: string) => _setIsBlocked(JSON.parse(isSubBlocked))}
            activeBgColor="red200"
          />
        </div>
      </ModalSubFeedbackBody>
      <ModalSubFeedbackFooter
        buttonPrimary={{
          onClick: handleOnNext,
          isDisabled: _isBlocked === undefined,
          text: translate("LABEL_NEXT"),
        }}
        buttonSecondary={{
          onClick: goBackToRatingStep,
          isDisabled: false,
          text: translate("LABEL_BACK"),
        }}
      />
    </>
  );
}
